/**
 * Shared constants for invoice filter options.
 *
 * These are used by FilterPopover and other components.
 */

import type { InvoiceStatus, PaymentStatus } from '@/types/invoices';

/**
 * Status options for display.
 */
export const STATUS_OPTIONS: { value: InvoiceStatus; label: string }[] = [
  { value: 'NEW', label: 'Новый' },
  { value: 'OCR_PENDING', label: 'OCR в процессе' },
  { value: 'OCR_COMPLETE', label: 'OCR завершен' },
  { value: 'OCR_FAILED', label: 'OCR ошибка' },
  { value: 'REVIEW_NEEDED', label: 'Требует проверки' },
  { value: 'DRAFT', label: 'Черновик' },
  { value: 'VERIFIED', label: 'Проверен' },
  { value: 'CANCELLED', label: 'Отменен' },
  { value: 'EXPORTED', label: 'Экспортирован' },
];

/**
 * Payment states (derived from money-ledger allocations, MD-0092) for filters.
 */
export const PAYMENT_STATUS_OPTIONS: { value: PaymentStatus; label: string }[] = [
  { value: 'unpaid', label: 'Не оплачен' },
  { value: 'partial', label: 'Частично оплачен' },
  { value: 'paid', label: 'Оплачен' },
  { value: 'overdue', label: 'Просрочен' },
  { value: 'not_required', label: 'Оплата не требуется' },
];

/**
 * Displayed payment states: the derived ones plus `unknown` (document total not known).
 */
const DISPLAYED_PAYMENT_STATUSES = [
  'unpaid',
  'partial',
  'paid',
  'overdue',
  'not_required',
  'unknown',
] as const;

export type DisplayedPaymentStatus = (typeof DISPLAYED_PAYMENT_STATUSES)[number];

const DISPLAYED_PAYMENT_STATUS_SET = new Set<string>(DISPLAYED_PAYMENT_STATUSES);

/**
 * Payment status pill configuration with colors.
 * Used for inline pills and group headers.
 */
export const PAYMENT_STATUS_PILL_CONFIG: Record<
  DisplayedPaymentStatus,
  { label: string; text: string }
> = {
  unpaid: {
    label: 'Не оплачен',
    text: 'text-foreground',
  },
  partial: {
    label: 'Частично оплачен',
    text: 'text-warning',
  },
  paid: {
    label: 'Оплачен',
    text: 'text-success',
  },
  overdue: {
    label: 'Просрочен',
    text: 'text-destructive',
  },
  not_required: {
    label: 'Не требуется',
    text: 'text-muted-foreground',
  },
  unknown: {
    label: 'Нет суммы',
    text: 'text-muted-foreground',
  },
};

/**
 * Type guard for DisplayedPaymentStatus.
 */
export function isDisplayedPaymentStatus(status: string | null): status is DisplayedPaymentStatus {
  return status !== null && DISPLAYED_PAYMENT_STATUS_SET.has(status);
}

/**
 * A document without a known total has no payment state.
 */
export function normalizePaymentStatus(status: PaymentStatus | null): DisplayedPaymentStatus {
  return status ?? 'unknown';
}

/**
 * Grouping options for display.
 */
export const GROUP_OPTIONS = [
  { value: 'none', label: 'Без группировки' },
  { value: 'supplier', label: 'По поставщику' },
  { value: 'doc_type', label: 'По типу документа' },
  { value: 'location', label: 'По локации' },
  { value: 'period', label: 'По периоду' },
  { value: 'payment', label: 'По оплате' },
] as const;

export type GroupOptionValue = (typeof GROUP_OPTIONS)[number]['value'];
